import { Booking, BookingStatus, Outing, PaymentStatus, RefundStatus, bookingOccupiesCapacity } from "../features/jacana/types";
import { isE2EMockAuthEnabled } from "./e2eAuth";

const JACANA_E2E_STATE_KEY = "jacanaE2EState";

type E2EJacanaState = {
  outings: Outing[];
  bookings: Booking[];
};

const EMPTY_STATE: E2EJacanaState = {
  outings: [],
  bookings: [],
};

function isEnabled() {
  return typeof window !== "undefined" && isE2EMockAuthEnabled();
}

function cloneDeep<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

function readState(): E2EJacanaState {
  if (!isEnabled()) return cloneDeep(EMPTY_STATE);

  try {
    const raw = window.localStorage.getItem(JACANA_E2E_STATE_KEY);
    if (!raw) return cloneDeep(EMPTY_STATE);
    const parsed = JSON.parse(raw) as Partial<E2EJacanaState>;
    return {
      outings: Array.isArray(parsed.outings) ? parsed.outings : [],
      bookings: Array.isArray(parsed.bookings) ? parsed.bookings : [],
    };
  } catch {
    return cloneDeep(EMPTY_STATE);
  }
}

function writeState(state: E2EJacanaState) {
  if (!isEnabled()) return;
  window.localStorage.setItem(JACANA_E2E_STATE_KEY, JSON.stringify(state));
  window.dispatchEvent(new Event("jacana-e2e-state-changed"));
}

function syncLegacyFields(booking: Booking): Booking {
  return {
    ...booking,
    status: booking.bookingStatus,
    isPaid: booking.paymentStatus === "paid" || booking.paymentStatus === "refunded",
  };
}

function persistBookingUpdate(state: E2EJacanaState, bookingId: string, updater: (booking: Booking) => Booking) {
  state.bookings = state.bookings.map((booking) => booking.id === bookingId ? syncLegacyFields(updater(booking)) : booking);
}

function getOutingIndex(state: E2EJacanaState, outingId: string) {
  return state.outings.findIndex((outing) => outing.id === outingId);
}

function releaseCapacityIfNeeded(state: E2EJacanaState, previousStatus: BookingStatus, booking: Booking) {
  if (!bookingOccupiesCapacity(previousStatus)) return;
  if (bookingOccupiesCapacity(booking.bookingStatus)) return;

  const outingIndex = getOutingIndex(state, booking.outingId);
  if (outingIndex < 0) return;

  const outing = state.outings[outingIndex];
  state.outings[outingIndex] = {
    ...outing,
    currentParticipants: Math.max(0, (outing.currentParticipants || 0) - (booking.participantsCount || 0)),
  };
}

function ensureBooking(state: E2EJacanaState, bookingId: string) {
  const booking = state.bookings.find((entry) => entry.id === bookingId);
  if (!booking) {
    throw new Error("Réservation introuvable.");
  }
  return booking;
}

function nowMs() {
  return Date.now();
}

function hoursUntilOuting(outing?: Outing | null) {
  if (!outing?.date) return null;
  return (new Date(outing.date).getTime() - nowMs()) / (60 * 60 * 1000);
}

function getOuting(state: E2EJacanaState, outingId: string) {
  return state.outings.find((entry) => entry.id === outingId) || null;
}

function bookingIsFinal(status: BookingStatus) {
  return ["cancelled", "expired", "failed"].includes(status);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null ? value as Record<string, unknown> : null;
}

function roundCurrency(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function getRefundableAmount(booking: Booking) {
  const moneyTotalsRefundable = Number((booking as Booking & {
    moneyTotals?: { totalRefundable?: number };
  }).moneyTotals?.totalRefundable);

  if (Number.isFinite(moneyTotalsRefundable)) {
    return Math.max(0, roundCurrency(moneyTotalsRefundable));
  }

  const partialRefundAmount = Number(booking.partialRefund?.amount);
  if (Number.isFinite(partialRefundAmount)) {
    return Math.max(0, roundCurrency(partialRefundAmount));
  }

  const refundAmountRequested = Number(booking.refundAmountRequested);
  if (Number.isFinite(refundAmountRequested)) {
    return Math.max(0, roundCurrency(refundAmountRequested));
  }

  const amount = Number(booking.amount);
  if (Number.isFinite(amount)) {
    return Math.max(0, roundCurrency(amount));
  }

  return 0;
}

function buildPartialRefundCommunication(bookingId: string) {
  return `Remboursement réservation ${bookingId}`;
}

function cancelBooking(params: { bookingId: string }) {
  const state = readState();
  const booking = ensureBooking(state, params.bookingId);
  const outing = getOuting(state, booking.outingId);

  if (bookingIsFinal(booking.bookingStatus)) {
    return { success: true, noop: true, bookingId: booking.id, bookingStatus: booking.bookingStatus, paymentStatus: booking.paymentStatus, refundStatus: booking.refundStatus };
  }

  const hours = hoursUntilOuting(outing);
  if (hours === null || hours <= 24) {
    throw new Error("L'annulation standard n'est plus disponible à moins de 24h avant la sortie.");
  }

  const previousStatus = booking.bookingStatus;
  const refundableAmount = getRefundableAmount(booking);
  const refundRequired = booking.paymentStatus === "paid" && refundableAmount > 0;

  persistBookingUpdate(state, booking.id, (current) => ({
    ...current,
    bookingStatus: "cancelled",
    paymentStatus: refundRequired ? current.paymentStatus : "expired",
    refundStatus: refundRequired ? "pending" : "not_applicable",
    partialRefundStatus: refundRequired ? "pending_customer_iban" : "not_applicable",
    refundAmountRequested: refundRequired ? refundableAmount : 0,
    partialRefund: refundRequired
      ? {
        status: "pending_customer_iban",
        amount: refundableAmount,
        ibanMasked: null,
        accountHolderName: null,
        communication: buildPartialRefundCommunication(current.id),
      }
      : current.partialRefund,
    cancelledAt: new Date().toISOString(),
    cancelledBy: "user",
  }));

  const updatedBooking = ensureBooking(state, booking.id);
  releaseCapacityIfNeeded(state, previousStatus, updatedBooking);
  writeState(state);

  return {
    success: true,
    bookingId: booking.id,
    bookingStatus: updatedBooking.bookingStatus,
    paymentStatus: updatedBooking.paymentStatus,
    refundStatus: updatedBooking.refundStatus,
    partialRefundStatus: updatedBooking.partialRefundStatus,
    refundAmountRequested: updatedBooking.refundAmountRequested,
    refundRequired,
  };
}

function submitPartialRefundIban(params: { bookingId: string; iban: string; accountHolderName?: string }) {
  const state = readState();
  const booking = ensureBooking(state, params.bookingId);
  const currentStatus = String(booking.partialRefund?.status || booking.partialRefundStatus || "not_applicable");

  if (!["pending_customer_iban", "pending_manual"].includes(currentStatus)) {
    throw new Error("Le statut actuel n'autorise pas la mise à jour de l'IBAN.");
  }

  const ibanRaw = String(params.iban || "").replace(/\s+/g, "").toUpperCase();
  if (!/^BE\d{14}$/.test(ibanRaw)) {
    throw new Error("IBAN invalide.");
  }

  const ibanMasked = `${ibanRaw.slice(0, 4)} **** **** ${ibanRaw.slice(-4)}`;
  const accountHolderName = String(params.accountHolderName || "").trim() || null;

  persistBookingUpdate(state, booking.id, (current) => ({
    ...current,
    partialRefundStatus: "pending_manual",
    partialRefund: {
      ...(current.partialRefund || {}),
      status: "pending_manual",
      amount: getRefundableAmount(current),
      ibanMasked,
      accountHolderName,
      communication: current.partialRefund?.communication || buildPartialRefundCommunication(current.id),
    },
    lastStatusUpdateAt: new Date().toISOString(),
  }));

  writeState(state);
  const updatedBooking = ensureBooking(state, booking.id);
  return {
    success: true,
    bookingId: booking.id,
    partialRefundStatus: updatedBooking.partialRefundStatus,
    ibanMasked,
  };
}

function reportAbsence(params: { bookingId: string }) {
  const state = readState();
  const booking = ensureBooking(state, params.bookingId);
  const outing = getOuting(state, booking.outingId);
  const hours = hoursUntilOuting(outing);

  if (booking.attendanceStatus === "reported_absent") {
    return { success: true, noop: true, bookingId: booking.id, attendanceStatus: booking.attendanceStatus };
  }

  if (booking.bookingStatus !== "confirmed" || hours === null || hours <= 0 || hours > 24) {
    throw new Error("Le signalement d'absence n'est disponible que dans les 24h avant la sortie.");
  }

  persistBookingUpdate(state, booking.id, (current) => ({
    ...current,
    attendanceStatus: "reported_absent",
    absenceReportedAt: new Date().toISOString(),
    absenceReportedBy: "user",
  }));

  writeState(state);
  const updatedBooking = ensureBooking(state, booking.id);
  return { success: true, bookingId: booking.id, attendanceStatus: updatedBooking.attendanceStatus };
}

function validateManualPayment(params: { bookingId: string }) {
  const state = readState();
  const booking = ensureBooking(state, params.bookingId);

  if (booking.paymentMethod !== "bank_transfer") {
    throw new Error("Cette réservation n'utilise pas le virement bancaire.");
  }

  if (booking.bookingStatus === "confirmed" && booking.paymentStatus === "paid") {
    return { success: true, noop: true, bookingId: booking.id, bookingStatus: booking.bookingStatus, paymentStatus: booking.paymentStatus };
  }

  persistBookingUpdate(state, booking.id, (current) => ({
    ...current,
    bookingStatus: "confirmed",
    paymentStatus: "paid",
    refundStatus: current.refundStatus || "not_applicable",
    lastStatusUpdateAt: new Date().toISOString(),
  }));

  writeState(state);
  const updatedBooking = ensureBooking(state, booking.id);
  return { success: true, bookingId: booking.id, bookingStatus: updatedBooking.bookingStatus, paymentStatus: updatedBooking.paymentStatus };
}

function processRefund(params: { bookingId: string; action?: "refund" | "refuse"; refundMethod?: "manual" | "provider"; refundReason?: string }) {
  const state = readState();
  const booking = ensureBooking(state, params.bookingId);
  const action = params.action || "refund";

  if (action === "refund" && booking.paymentStatus === "refunded" && booking.refundStatus === "refunded") {
    return { success: true, noop: true, bookingId: booking.id, paymentStatus: booking.paymentStatus, refundStatus: booking.refundStatus };
  }

  persistBookingUpdate(state, booking.id, (current) => ({
    ...current,
    paymentStatus: action === "refund" ? "refunded" : current.paymentStatus,
    refundStatus: action === "refund" ? "refunded" : "refused",
    refundMethod: action === "refund" ? (params.refundMethod || "manual") : current.refundMethod,
    refundReason: params.refundReason || current.refundReason,
    refundedAt: action === "refund" ? new Date().toISOString() : current.refundedAt,
  }));

  writeState(state);
  const updatedBooking = ensureBooking(state, booking.id);
  return { success: true, bookingId: booking.id, paymentStatus: updatedBooking.paymentStatus, refundStatus: updatedBooking.refundStatus };
}

function cancelOutingAndCascade(params: { outingId: string; reason?: string; sendEmails?: boolean; organizerMessage?: string }) {
  const state = readState();
  let processedCount = 0;
  let releasedParticipants = 0;

  state.bookings = state.bookings.map((booking) => {
    if (booking.outingId !== params.outingId || bookingIsFinal(booking.bookingStatus)) {
      return booking;
    }

    processedCount += 1;
    if (bookingOccupiesCapacity(booking.bookingStatus)) {
      releasedParticipants += booking.participantsCount || 0;
    }

    const nextPaymentStatus: PaymentStatus = booking.paymentStatus === "paid"
      ? "paid"
      : booking.paymentStatus === "pending"
        ? "failed"
        : booking.paymentStatus;

    const nextRefundStatus: RefundStatus = booking.paymentStatus === "paid" ? "pending" : "not_applicable";

    return syncLegacyFields({
      ...booking,
      bookingStatus: "cancelled",
      paymentStatus: nextPaymentStatus,
      refundStatus: nextRefundStatus,
      cancelledAt: new Date().toISOString(),
      cancelledBy: "admin",
      cancellationReason: params.reason || "organisation_cancellation",
      organizerCancellationMessage: params.organizerMessage,
      refundReason: params.reason || booking.refundReason,
    });
  });

  const outingIndex = getOutingIndex(state, params.outingId);
  if (outingIndex >= 0) {
    const outing = state.outings[outingIndex];
    state.outings[outingIndex] = {
      ...outing,
      deleted: true,
      organizerCancellationMessage: params.organizerMessage,
      currentParticipants: Math.max(0, (outing.currentParticipants || 0) - releasedParticipants),
    };
  }

  writeState(state);

  return {
    success: true,
    outingId: params.outingId,
    processedCount,
    releasedParticipants,
  };
}

function markAttendance(params: { bookingId: string; attendanceStatus: "attended" | "no_show" }) {
  const state = readState();
  const booking = ensureBooking(state, params.bookingId);

  persistBookingUpdate(state, booking.id, (current) => ({
    ...current,
    attendanceStatus: params.attendanceStatus,
    lastStatusUpdateAt: new Date().toISOString(),
  }));

  writeState(state);
  const updatedBooking = ensureBooking(state, booking.id);
  return { success: true, bookingId: booking.id, attendanceStatus: updatedBooking.attendanceStatus };
}

function submitSupportTicketMock(params: unknown) {
  const paramsRecord = asRecord(params);
  const metadata = asRecord(paramsRecord?.metadata);
  const environment = typeof metadata?.environment === "string" && metadata.environment.trim()
    ? metadata.environment
    : "dev";

  return {
    success: true,
    ticketId: `mock-ticket-${Date.now()}`,
    status: "open",
    githubSyncStatus: "synced",
    githubIssueNumber: 999,
    githubIssueUrl: "https://github.com/blaise6640/Jacana_guide_nature/issues/999",
    environment,
    emailNotificationStatus: "sent",
  };
}

export const e2eJacanaMock = {
  isEnabled,
  getAllOutings() {
    return readState().outings.filter((outing) => !outing.deleted);
  },
  getOutingById(id: string) {
    return readState().outings.find((outing) => outing.id === id) || null;
  },
  getPastOutings(limitCount: number = 8, referenceDate?: string) {
    const referenceTimeMs = referenceDate ? new Date(referenceDate).getTime() : nowMs();
    return readState().outings
      .filter((outing) => !outing.deleted && new Date(outing.date).getTime() < referenceTimeMs)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, limitCount);
  },
  getAllBookings() {
    return readState().bookings;
  },
  getBookingById(id: string) {
    return readState().bookings.find((booking) => booking.id === id) || null;
  },
  getBookingsByOuting(outingId: string) {
    return readState().bookings.filter((booking) => booking.outingId === outingId);
  },
  getBookingsByUserEmail(email: string) {
    const normalized = email.trim().toLowerCase();
    return readState().bookings.filter((booking) => booking.userEmail.trim().toLowerCase() === normalized);
  },
  runAction(name: string, params: unknown) {
    switch (name) {
      case "cancelBooking":
        return cancelBooking(params as { bookingId: string });
      case "reportAbsence":
        return reportAbsence(params as { bookingId: string });
      case "validateManualPayment":
        return validateManualPayment(params as { bookingId: string });
      case "processRefund":
        return processRefund(params as { bookingId: string; action?: "refund" | "refuse"; refundMethod?: "manual" | "provider"; refundReason?: string });
      case "cancelOutingAndCascade":
        return cancelOutingAndCascade(params as { outingId: string; reason?: string; sendEmails?: boolean; organizerMessage?: string });
      case "markAttendance":
        return markAttendance(params as { bookingId: string; attendanceStatus: "attended" | "no_show" });
      case "submitPartialRefundIban":
        return submitPartialRefundIban(params as { bookingId: string; iban: string; accountHolderName?: string });
      case "submitSupportTicket":
        return submitSupportTicketMock(params);
      default:
        return null;
    }
  },
};
